import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Database, Trash2, Loader2 } from 'lucide-react';
import {
  getBacklog,
  deleteBacklogJob,
  replayBacklogJob,
  updateBacklogJob,
  type BacklogJob,
} from '../lib/api/provider_health.js';

/**
 * Lists pipeline_jobs currently held waiting for an AI provider to recover.
 * Drained automatically when provider state returns to healthy.
 */
export function BacklogPanel() {
  const queryClient = useQueryClient();
  const backlogQuery = useQuery({
    queryKey: ['backlog'],
    queryFn: getBacklog,
    refetchInterval: 15_000,
  });

  const deleteMut = useMutation({
    mutationFn: deleteBacklogJob,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backlog'] }),
  });
  const replayMut = useMutation({
    mutationFn: replayBacklogJob,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backlog'] }),
  });
  const skipMut = useMutation({
    mutationFn: ({ jobId, until }: { jobId: string; until: string }) =>
      updateBacklogJob(jobId, { skip_until: until }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backlog'] }),
  });

  if (backlogQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 text-grey-400 animate-spin" />
      </div>
    );
  }

  const data = backlogQuery.data;
  if (!data || data.total === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-grey-400">
        <Database className="h-8 w-8 mb-2" />
        <p className="text-sm">No held jobs — all providers healthy or no work waiting.</p>
      </div>
    );
  }

  const skipPresets = (jobId: string) => {
    const now = new Date();
    const oneHour = new Date(now.getTime() + 60 * 60 * 1000);
    const tomorrow9 = new Date(now);
    tomorrow9.setDate(tomorrow9.getDate() + 1);
    tomorrow9.setHours(9, 0, 0, 0);
    const dayOfWeek = now.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : ((8 - dayOfWeek) % 7) || 7;
    const monday9 = new Date(now);
    monday9.setDate(monday9.getDate() + daysUntilMonday);
    monday9.setHours(9, 0, 0, 0);
    const showMonday = ![1, 2, 3].includes(dayOfWeek);

    return (
      <div className="flex gap-1">
        <button
          onClick={() => skipMut.mutate({ jobId, until: oneHour.toISOString() })}
          className="text-xs px-2 py-1 bg-grey-100 hover:bg-grey-200 rounded"
          title="Skip until 1 hour from now"
        >Skip 1h</button>
        <button
          onClick={() => skipMut.mutate({ jobId, until: tomorrow9.toISOString() })}
          className="text-xs px-2 py-1 bg-grey-100 hover:bg-grey-200 rounded"
          title="Skip until 9:00 AM tomorrow Sydney"
        >Tomorrow 9am</button>
        {showMonday && (
          <button
            onClick={() => skipMut.mutate({ jobId, until: monday9.toISOString() })}
            className="text-xs px-2 py-1 bg-grey-100 hover:bg-grey-200 rounded"
            title="Skip until 9:00 AM Monday Sydney"
          >Mon 9am</button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-grey-600">
        {data.total} job(s) held waiting for AI provider recovery. Drained automatically when provider state returns to healthy.
      </p>
      {Object.entries(data.groups).map(([provider, jobs]) => (
        <div key={provider} className="border border-grey-200 rounded-lg">
          <div className="bg-grey-50 px-4 py-2 border-b border-grey-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-grey-700">
              Waiting on <span className="font-mono">{provider}</span> ({jobs.length})
            </h3>
          </div>
          <div className="divide-y divide-grey-100">
            {jobs.map((job) => (
              <BacklogRow
                key={job.job_id}
                job={job}
                onDelete={() => deleteMut.mutate(job.job_id)}
                onReplay={() => replayMut.mutate(job.job_id)}
                skipPresets={skipPresets}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function BacklogRow({
  job,
  onDelete,
  onReplay,
  skipPresets,
}: {
  job: BacklogJob;
  onDelete: () => void;
  onReplay: () => void;
  skipPresets: (jobId: string) => React.ReactNode;
}) {
  const heldSince = job.held_since ? format(new Date(job.held_since), 'dd MMM HH:mm') : '—';
  const skipUntil = job.skip_until ? format(new Date(job.skip_until), 'dd MMM HH:mm') : null;
  const isSkipped = skipUntil && new Date(job.skip_until!) > new Date();
  const payloadHint = (() => {
    const p = job.payload || {};
    return (
      (p.announcement_id as string) ||
      (p.plan_item_id as string) ||
      (p.item_id as string) ||
      JSON.stringify(p).slice(0, 60)
    );
  })();

  return (
    <div className={`px-4 py-2 flex items-center gap-3 ${isSkipped ? 'opacity-50' : ''}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-grey-900">{job.job_type}</span>
          <span className="text-xs text-grey-500">priority {job.priority}</span>
          <span className="text-xs text-grey-400">held since {heldSince}</span>
          {skipUntil && (
            <span className="text-xs text-amber-600">skip until {skipUntil}</span>
          )}
        </div>
        <div className="text-xs text-grey-500 truncate" title={payloadHint}>
          {payloadHint}
        </div>
        {job.error_class && (
          <div className="text-xs text-red-600 truncate" title={job.error_message ?? ''}>
            {job.error_class}: {(job.error_message ?? '').slice(0, 80)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {skipPresets(job.job_id)}
        <button
          onClick={onReplay}
          className="text-xs px-2 py-1 bg-primary-50 text-primary-700 hover:bg-primary-100 rounded"
        >
          Replay
        </button>
        <button
          onClick={() => {
            if (confirm(`Delete held job ${job.job_id}? This will mark its plan item as skipped.`)) {
              onDelete();
            }
          }}
          className="text-xs px-2 py-1 bg-red-50 text-red-700 hover:bg-red-100 rounded inline-flex items-center gap-1"
        >
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      </div>
    </div>
  );
}
